import { DEFAULT_WEIGHT } from "../Constants";

export interface OperationResult<T> {
    Result: T,
    HasErrors: boolean,
    Errors: string[],
}

export interface BaseEntity {
    Id: string;
    IsDeleted: boolean;
    LastUpdateDate: string;
}

export interface IProficiency {
    Id: string;
    OrganizationId: string | null;
    TemplateRevisionId: string | null;
    ParentProficiencyId: string | null;
    ProficiencyGroupId: string | null;
    Type: ProficiencyType;
    Name: string;
    Weight: number;
    IsNegative: boolean;
    Notes: string;
    IsDeleted: boolean;
    LastUpdateDate: string;
}

export class Proficiency implements IProficiency {
    Id: string = "";
    OrganizationId: null;
    TemplateRevisionId: null;
    ParentProficiencyId: null;
    ProficiencyGroupId: null;
    Type: ProficiencyType;
    Name: string = "";
    Weight: number = DEFAULT_WEIGHT;
    IsNegative: boolean = false;
    Notes: string;
    IsDeleted: boolean = false;
    LastUpdateDate: string = "";

}

export interface ICreateProficiency {
    TemplateRevisionId: string | null;
    ProficiencyGroupId: string | null;
    AssociationId: string | null;
    Type: ProficiencyType;
    Name: string;
    Weight: number;
    IsNegative: boolean;
}

export enum ProficiencyType {
    NA = "",
    Behavior = 1,
    Ability = 2,
}

export enum AbilityLevel {
    Unskilled = 0,
    Beginner = 1,
    Intermediate = 2,
    Advanced = 3,
    Expert = 4
}

export enum BehaviorLevel {
    Never = 0,
    Rarely = 1,
    Sometimes = 2,
    Usually = 3,
    Always = 4
}

export enum SelectionLevel {
    Unskilled, Beginner, Intermediate, Advanced, Expert,
    Never, Rarely, Sometimes, Usually, Always
}

export enum WeightSelection {
    MostImportant = "MostImportant",
    StandardPlusFour = "StandardPlusFour",
    StandardPlusThree = "StandardPlusThree",
    StandardPlusTwo = "StandardPlusTwo",
    StandardPlusOne = "StandardPlusOne",
    Standard = "Standard",
    StandardLessOne = "StandardLessOne",
    StandardLessTwo = "StandardLessTwo",
    StandardLessThree = "StandardLessThree",
    LeastImportant = "LeastImportant",
}

export interface UpdateProficiencySelection {
    ProficiencyId: string;
    ProficiencySelectionId: string;
    Selection: string;
    Notes: string;
}

export interface TemplateProficiency {
    TemplateId: string,
    ProficiencyId: string,
    ProficiencyName: string,
    Type: ProficiencyType;
    Weight: number;
    ProficiencyGroupId: string
}

export enum ProcessType {
    Evaluate,
    Interview,
}

export interface ProficiencySelection {
    Type: string;
    SelectionText: string;
    Value: number;
}

export interface IProficiencyGraph {
    ProficiencyId: string;
    TemplateId: string | null;
    AssociationId: string | null;
    ProficiencyGroupId: string | null;
    ProficiencyGroupName: string;
    Type: string;
    Name: string;
    Selection: ProficiencySelection;
    Weight: WeightSelection;
    IsNegative: boolean;
    exclude: boolean;
    Notes: string;
}

export class ProficiencyGraph implements IProficiencyGraph {
    ProficiencySelectionId: string;
    ProficiencyId: string;
    TemplateId: string;
    AssociationId: string;
    ProficiencyGroupId: string;
    ProficiencyGroupName: string;
    Type: string;
    Name: string;
    Selection: ProficiencySelection;
    Weight: WeightSelection;
    IsNegative: boolean;
    exclude: boolean;
    Notes: string;

}

export interface TemplateGraph {
    TemplateId: string;
    Revision: number;
    ProcessType: ProcessType;
    PositionId: string;
    PositionTitle: string;
    Proficiencies: ProficiencyGraph[];
}

export interface OngoingAssessmentGraph {
    Assessment: OngoingAssessment;
    TemplateName: string;
    Talent: Talent;
    Proficiencies: ProficiencyGraph[];
    ScoreCard: ScoreCard;
}

export interface IOngoingAssessment extends BaseEntity {
    TemplateId: string;
    TalentId?: string;
    IsMock: boolean;
}

export class OngoingAssessment implements IOngoingAssessment {
    Id: string = "";
    IsDeleted: boolean = false;
    LastUpdateDate: string = "";
    TemplateId: string = "";
    TalentId?: string;
    IsMock: boolean = true;
}

export interface TalentDisplay extends Talent {
    PositionTitle: string
    PositionId: string;
    FirstName: string;
    LastName: string;
    Email: string;
    OrganizationIdentifier: string;
}

export interface Talent extends BaseEntity {
    OrganizationId: string;
    PositionId: string;
    FirstName: string;
    LastName: string;
    Email: string;
    OrganizationIdentifier: string;
    AssessorId: string;
    SystemUserId: string;
    IsActive: boolean;
    ActivatedDate?: string;
    DeactivatedDate?: string;
    ActivationHistory?: TalentActivationPeriod[];
}

export interface TalentActivationPeriod {
    ActivatedDate: string;
    DeactivatedDate?: string;
}

export interface TalentGraph extends Talent {
    PositionTitle: string;
    Roles: string[];
    Incidents: Incident[];
    PerformanceItems: PerformanceItem[];
}

export interface PerformanceItem extends BaseEntity {
    TalentId: string;
    SystemUserId: string;
    Title: string;
    Description: string;
    Severity: SeverityLevel;
    State: ActiveState;
    ActiveDate: string;
    ResolvedDate: string;
    Incidents: Incident[];
}

export enum SeverityLevel {
    Low = 0,
    Medium = 1,
    High = 2
}

export enum ActiveState {
    Active = 'Active',
    Resolved = 'Resolved'
}

export interface Incident extends BaseEntity {
    TalentId: string;
    PerformanceItemId?: string;
    Type: string;
    Title: string;
    Description: string;
    OccurrenceDate: any;
}

export interface IncidentReview extends Incident {
    apply: boolean;
    isReviewed: boolean;
    severity?: SeverityLevel;
}

export interface ScoreCard {
    OverallScore: number;
    AdjustedScore: number;
    AdjustmentAmount: number;
    ProficiencyGroupScores: ProficiencyGroupScore[];
    PerformanceItems: PerformanceItem[];
}

export interface ScoreAdjustment {
    AssessmentScore: number;
    AdjustedScore: number;
}

export interface ProficiencyGroupScore {
    ProficiencyGroup: string;
    Score: number;
    Weight: number;
}

export interface OngoingAssessmentDisplay extends OngoingAssessment {
    TemplateName: string;
    TalentName: string;
    PositionTitle: string;
    TalentEmail: string;
}

export interface IPosition extends BaseEntity {
    Title: string;
    Description: string;
}

export class Position implements IPosition {
    Title: string = '';
    Description: string = '';
    Id: string = '';
    IsDeleted: boolean = false;
    LastUpdateDate: string = '';
}

export enum NoteAssociationType {
    Talent = 1,
    OngoingAssessment = 2,
}
export interface CreateNote {
    AssociationId: string;
    AssociationType: NoteAssociationType;
    Title: string;
    Content: string;
}

export interface Note extends BaseEntity {
    AssociationId: string;
    AssociationType: string;
    SystemUserId: string;
    Title: string;
    Content: string;
}

export interface NoteDisplay extends Note {
    SystemUserName: string;
}

export interface OngoingAssessment {
        TemplateRevisionId: string;
        TemplateName: string;
        Talent: Talent;
        Proficiencies: ProficiencyGraph[];
        ScoreCard: ScoreCard;
}

export interface ImprovementItem {
    Proficiency: Proficiency;
    CurrentSelection: string;
    NextSelection: string;
}

export interface ImprovementPlan{
    TalentId: string;
    ImprovementItems: ImprovementItem[];
}

export interface DevelopmentItem {
    Id: string;
    TalentId: string;
    ProficiencyId: string;
    Title: string;
    Narrative: string;
    StartDate: string;
    CompleteDate: string;
}

export interface DevelopmentPlan {
    Id: string;
    TalentId: string;
    AssessmentId: string;
    DevelopmentPlanItems: DevelopmentPlanItem[];
}

export interface DevelopmentPlanItem {
    Id: string;
    AssociationId: string;
    Type: string;
    Name: string;
    Narrative: string;
    NarrativePrompt: string;
}

export interface TemplateRevision2ProficiencyGroup {
    Id: string;
    TemplateRevisionId: string;
    ProficiencyGroupId: string;
    Weight: number;
}

export interface TemplateRevision2ProficiencyGroupDisplay extends TemplateRevision2ProficiencyGroup {
    TemplateName: string;
    ProficiencyGroupName: string;
}
